/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';

import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Box, Typography } from '@material-ui/core';

import { Branding } from '../../branding';
import AppSnackbar from '../../components/Snackbar/Snackbar';
import PasswordInputField, { isPasswordMatch, validatePasswordRules, PASSWORD_RULES } from '../PasswordInputField/PasswordInputField';

const useStyles = makeStyles(() => ({
	dialogActions: {
		paddingRight: 22,
	},
	box: {
		textAlign: 'center',
		marginBottom: 20,
	},
	input: {
		minWidth: 300,
	},
	buttonSave: {
		color: Branding.positive
	},
	buttonCancel: {
		color: Branding.negative
	},
	passwordHint: {
		fontSize: '0.8rem',
		color: '#666',
		marginBottom: 8,
		textAlign: 'left',
	},
	validationError: {
		fontSize: '0.8rem',
		color: Branding.negative,
		marginTop: 4,
		textAlign: 'left',
	},
	validationSuccess: {
		fontSize: '0.8rem',
		color: Branding.positive,
		marginTop: 4,
		textAlign: 'left',
	},
}));

const ChangePasswordDialog = (props) => {
	const classes = useStyles();
	const [oldPassword, setOldPassword] = React.useState({
		password: '',
		showPassword: false
	});
	const [newPassword, setNewPassword] = React.useState({
		password: '',
		showPassword: false
	});
	const [confirmPassword, setConfirmPassword] = React.useState({
		password: '',
		showPassword: false
	});
	const [snackBarOps, setSnackBarOps] = React.useState({
		type: 'info',
		open: false,
		vertical: 'top',
		horizontal: 'center',
		autoHide: 0,
		message: ''
	});

	// パスワードのバリデーション結果を取得
	const passwordValidation = validatePasswordRules(newPassword.password);
	const passwordsMatch = isPasswordMatch(newPassword.password, confirmPassword.password);
	const canSubmit = oldPassword.password.length > 0 && passwordValidation.isValid && passwordsMatch;

	const changePassword = (oldPwd, newPwd) => {
		if (!oldPwd || !newPwd) {
			setSnackBarOps({
				type: 'error',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('CHANGE_PASSWORD_MESSAGE_EROR')
			})
			return;
		}

		// 新しいパスワードのルールチェック
		const validation = validatePasswordRules(newPwd);
		if (!validation.isValid) {
			setSnackBarOps({
				type: 'error',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 5000,
				message: validation.messages.join('、')
			})
			return;
		}

		Auth.currentAuthenticatedUser()
			.then(CognitoUser => {
				Auth.changePassword(CognitoUser, oldPwd, newPwd)
					.then((data) => {
						handleCancel(data === 'SUCCESS');
					})
					.catch((err) => {
						console.log(err);

						setSnackBarOps({
							type: 'error',
							open: true,
							vertical: 'top',
							horizontal: 'center',
							autoHide: 3000,
							message: I18n.get('CHANGE_PASSWORD_MESSAGE_EROR')
						})
					})
			})
			.catch(err => {
				console.log(err);

				setSnackBarOps({
					type: 'error',
					open: true,
					vertical: 'top',
					horizontal: 'center',
					autoHide: 3000,
					message: I18n.get('CHANGE_PASSWORD_MESSAGE_EROR')
				})
			})
	};

	const handleClickSave = () => {
		changePassword(oldPassword.password, newPassword.password)
	};

	const handleCancel = (succesful = false) => {
		setOldPassword({ ...oldPassword, password: '' });
		setNewPassword({ ...newPassword, password: '' });
		setConfirmPassword({ ...confirmPassword, password: '' });
		props.close(succesful);
	};

	return (
		<div>
			{snackBarOps.open && (
				<AppSnackbar ops={snackBarOps} />
			)}

			<Dialog
				open={props.open}
				onClose={handleCancel}
				disableBackdropClick
				aria-labelledby="change-password-dialog"
			>
				<DialogTitle id="change-password-dialog-title">
					{I18n.get('CHANGE_PASSWORD_TITLE')}
				</DialogTitle>
				<DialogContent className={classes.dialogContent}>
					<DialogContentText>
						{I18n.get('CHANGE_PASSWORD_DESCRIPTION')}
					</DialogContentText>
					<Box className={classes.box}>
						<PasswordInputField
							id="inputOldPassword"
							label={I18n.get('CHANGE_PASSWORD_OLDPASSWORD_INPUT_LABEL')}
							value={oldPassword.password}
							showPassword={oldPassword.showPassword}
							onChange={(value) => setOldPassword({ ...oldPassword, password: value })}
							onToggleShowPassword={() => setOldPassword({ ...oldPassword, showPassword: !oldPassword.showPassword })}
							autoFocus
							inputClassName={classes.input}
						/>
					</Box>
					<Box className={classes.box}>
						<Typography className={classes.passwordHint}>
							※ {PASSWORD_RULES.MIN_LENGTH}〜{PASSWORD_RULES.MAX_LENGTH}文字、英大文字・小文字・数字・記号のうち2種類以上
						</Typography>
						<PasswordInputField
							id="inputNewPassword"
							label={I18n.get('CHANGE_PASSWORD_NEWPASSWORD_INPUT_LABEL')}
							value={newPassword.password}
							showPassword={newPassword.showPassword}
							onChange={(value) => setNewPassword({ ...newPassword, password: value })}
							onToggleShowPassword={() => setNewPassword({ ...newPassword, showPassword: !newPassword.showPassword })}
							inputClassName={classes.input}
						/>
						{newPassword.password && !passwordValidation.isValid && (
							<div>
								{passwordValidation.messages.map((msg, index) => (
									<Typography key={index} className={classes.validationError}>
										{msg}
									</Typography>
								))}
							</div>
						)}
						{newPassword.password && passwordValidation.isValid && (
							<Typography className={classes.validationSuccess}>
								パスワードルールを満たしています
							</Typography>
						)}
					</Box>
					<Box className={classes.box}>
						<PasswordInputField
							id="inputConfirmPassword"
							label={I18n.get('CHANGE_PASSWORD_CONFIRM_PASSWORD_INPUT_LABEL')}
							value={confirmPassword.password}
							showPassword={confirmPassword.showPassword}
							onChange={(value) => setConfirmPassword({ ...confirmPassword, password: value })}
							onToggleShowPassword={() => setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword })}
							inputClassName={classes.input}
						/>
						{confirmPassword.password && !passwordsMatch && (
							<Typography className={classes.validationError}>
								パスワードが一致しません
							</Typography>
						)}
						{confirmPassword.password && passwordsMatch && (
							<Typography className={classes.validationSuccess}>
								パスワードが一致しています
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions className={classes.dialogActions}>
					<Button
						variant="outlined"
						onClick={handleClickSave}
						className={classes.buttonSave}
						disabled={!canSubmit}
					>
						{I18n.get('CHANGE_PASSWORD_SAVE_BUTTON_LABEL')}
					</Button>
					<Button
						variant="outlined"
						onClick={handleCancel}
						className={classes.buttonCancel}
					>
						{I18n.get('CHANGE_PASSWORD_CANCEL_BUTTON_LABEL')}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}

export default ChangePasswordDialog;
